import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    Select,
    MenuItem,
    Modal,
    Typography,
    Tooltip,
    IconButton,
    FormHelperText
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { apiCreateForward } from '@store';
import { showToast } from '@utils';
import {
    delivery_period_options,
    months_options,
    price_type_options,
    years_options
} from '../../../data';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import UploadFileModal from './UploadFileModal';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const Forward = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state?.user);
    const { allUsers } = useSelector((state) => state?.user);
    const { portfolio } = useSelector((state) => state?.portfolio);
    const { counterParty } = useSelector((state) => state?.counterParty);
    const { brokers } = useSelector((state) => state?.broker);
    // const { endpoints } = useSelector((state) => state?.endpoints);
    const { exchanges } = useSelector((state) => state?.exchanges);

    const initialState = {
        instrument_type: 'forward',
        transaction_type: '',
        trade_type: '',
        trader: '',
        portfolio: '',
        counterparty: '',
        reference: '',
        trade_date: '',
        settlement_time: '',
        payment_date: '',
        accounting_date: '',
        exchange: '',
        broker: '',
        status: '',
        comments: '',
        price_type: '',
        endpoint: '',
        payment_frequency: 'Monthly',
        delivery_period_sym: '',
        delivery_period_month: '',
        delivery_period_year: '',
        start_date: '',
        end_date: '',
        currency: '',
        price: '',
        volume: '',
        capacity: ''
    };

    //for development initialize the initial states with some values. if select is required, then it can be empty
    // const initialState = {
    //     instrument_type: 'forward',
    //     transaction_type: 'buy',
    //     trade_type: 'Physical',
    //     trader: '',
    //     portfolio: '',
    //     counterparty: '',
    //     reference: '',
    //     trade_date: dayjs().format('YYYY-MM-DD'),
    //     settlement_time: '',
    //     payment_date: '',
    //     accounting_date: '',
    //     exchange: '',
    //     broker: '',
    //     status: 'New',
    //     comments: '',
    //     price_type: 'Fixed',
    //     endpoint: '',
    //     payment_frequency: 'Monthly',
    //     delivery_period_sym: 'DA',
    //     delivery_period_month: '',
    //     delivery_period_year: dayjs().format('YYYY'),
    //     start_date: dayjs().format('YYYY-MM-DD'),
    //     end_date: dayjs().format('YYYY-MM-DD'),
    //     currency: 'EUR',
    //     price: '',
    //     volume: '',
    //     capacity: ''
    // };

    const [state, setState] = useState(initialState);
    const [errors, setErrors] = useState({});

    const validateField = (name) => {
        const s = state;
        const sym = s.delivery_period_sym;
        const yearSym =
            sym === 'MONTH' ||
            sym === 'SUMMER' ||
            sym === 'WINTER' ||
            sym === 'Q1' ||
            sym === 'Q2' ||
            sym === 'Q3' ||
            sym === 'Q4' ||
            sym === 'CAL' ||
            sym === 'GY';

        const isEmpty = (v) => v === '' || v === null || v === undefined;

        switch (name) {
            case 'transaction_type':
                return isEmpty(s.transaction_type)
                    ? 'Transaction Type is required'
                    : '';
            case 'trade_type':
                return isEmpty(s.trade_type) ? 'Trade Type is required' : '';
            case 'trader':
                return !s.trader ? 'Trader is required' : '';
            case 'portfolio':
                return !s.portfolio ? 'Portfolio is required' : '';
            case 'counterparty':
                return !s.counterparty ? 'Counterparty is required' : '';
            case 'trade_date':
                return isEmpty(s.trade_date) ? 'Trade Date is required' : '';
            case 'exchange':
                return !s.exchange ? 'Exchange is required' : '';
            case 'broker':
                return !s.broker ? 'Broker is required' : '';
            case 'status':
                return isEmpty(s.status) ? 'Status is required' : '';
            case 'price_type':
                return isEmpty(s.price_type) ? 'Price Type is required' : '';
            case 'endpoint':
                return !s.endpoint ? 'Endpoint is required' : '';
            case 'currency':
                return isEmpty(s.currency) ? 'Currency is required' : '';
            case 'delivery_period_sym':
                return isEmpty(sym) ? 'Delivery Period is required' : '';
            case 'delivery_period_year':
                return yearSym && isEmpty(s.delivery_period_year)
                    ? 'Year is required'
                    : '';
            case 'delivery_period_month':
                return sym === 'MONTH' && isEmpty(s.delivery_period_month)
                    ? 'Month is required'
                    : '';
            case 'start_date':
                return (sym === 'DA' || sym === 'BOM' || sym === 'MANUAL') &&
                    isEmpty(s.start_date)
                    ? 'Start Date is required'
                    : '';
            case 'end_date':
                return sym === 'MANUAL' && isEmpty(s.end_date)
                    ? 'End Date is required'
                    : '';
            case 'capacity':
                return isEmpty(s.capacity) && isEmpty(s.volume)
                    ? 'Capacity or Volume is required'
                    : s.capacity !== '' && Number(s.capacity) <= 0
                      ? 'Capacity must be greater than 0'
                      : '';
            case 'volume':
                return isEmpty(s.capacity) && isEmpty(s.volume)
                    ? 'Capacity or Volume is required'
                    : s.volume !== '' && Number(s.volume) <= 0
                      ? 'Volume must be greater than 0'
                      : '';
            case 'price':
                if (s.price_type === 'Fixed') {
                    if (isEmpty(s.price)) return 'Price is required';
                    if (Number(s.price) <= 0)
                        return 'Price must be greater than 0';
                }
                return '';
            default:
                return '';
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setErrors((prev) => ({ ...prev, [name]: validateField(name) }));
    };

    useEffect(() => {
        if (allUsers?.length && user?.FullName && !state.trader) {
            const loggedInUser = allUsers.find(
                (u) =>
                    u.fullname?.toLowerCase() === user.FullName?.toLowerCase()
            );
            if (loggedInUser) {
                setState((prevState) => ({
                    ...prevState,
                    trader: loggedInUser
                }));
            }
        }
    }, [allUsers, user, state.trader]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
        setState({
            ...state,
            [name]: value,
            ...(name === 'portfolio'
                ? { currency: value?.currency || '' }
                : {}),
            ...(name === 'price_type' &&
            (value === 'DA Index' || value === 'MA Index')
                ? { price: '' }
                : {}),
            ...(name === 'delivery_period_sym' && value !== 'MONTH'
                ? { delivery_period_month: '' }
                : {})
        });
    };

    const handleDateChange = (name, value) => {
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
        setState({
            ...state,
            [name]: value ? value.format('YYYY-MM-DD') : ''
        });
    };

    const handleNumericChange = (e) => {
        const { name, value } = e.target;
        const decimals =
            name === 'price'
                ? 3
                : name === 'volume'
                  ? 2
                  : name === 'capacity'
                    ? 2
                    : null;
        if (decimals === null) {
            handleChange(e);
            return;
        }
        const regex = new RegExp(`^\\d*\\.?\\d{0,${decimals}}$`);
        if (value === '' || regex.test(value)) {
            if (errors[name]) {
                setErrors({ ...errors, [name]: '' });
            }
            setState({ ...state, [name]: value });
        }
    };

    const [openModal, setOpenModal] = useState(false);

    const handleUploadFile = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        for (const field of [
            'transaction_type',
            'trade_type',
            'trader',
            'portfolio',
            'counterparty',
            'trade_date',
            'exchange',
            'broker',
            'status',
            'price_type',
            'endpoint',
            'currency',
            'delivery_period_sym',
            'delivery_period_year',
            'delivery_period_month',
            'start_date',
            'end_date',
            'capacity',
            'volume',
            'price'
        ]) {
            const msg = validateField(field);
            if (msg) newErrors[field] = msg;
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            showToast('error', 'Please fill all required fields correctly');
            return;
        }

        const data = { ...state };
        data.trade_date = new Date(data?.trade_date).toISOString();
        data.start_date =
            data?.start_date === ''
                ? ''
                : new Date(data?.start_date).toISOString();
        data.end_date =
            data?.end_date === '' ? '' : new Date(data?.end_date).toISOString();
        data.price = parseFloat(data?.price);
        data.capacity = parseInt(data?.capacity);
        data.volume = parseFloat(data?.volume);
        data.delivery_period_year = parseInt(data?.delivery_period_year);

        for (const key in data) {
            if (
                data[key] === '' ||
                data[key] === null ||
                data[key] === undefined
            ) {
                delete data[key];
            }
        }

        const response = await dispatch(apiCreateForward(data));
        if (response.meta.requestStatus === 'fulfilled') {
            showToast('success', 'Forward created successfully');
            setState(initialState);
        } else if (response.meta.requestStatus === 'rejected') {
            showToast('error', response.payload);
        }
    };

    const handleClear = () => {
        setState(initialState);
    };

    return (
        <>
            <Modal open={openModal} onClose={handleCloseModal}>
                <UploadFileModal handleClose={handleCloseModal} />
            </Modal>
            <Box className="border bg-white h-full">
                <Box className="px-8 py-4 border-b-2 flex justify-between items-center">
                    <Typography fontSize={'large'}>
                        Create New Forward
                    </Typography>
                    <Tooltip title="Upload File">
                        <IconButton onClick={handleUploadFile}>
                            <UploadOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box className="px-8 py-4">
                    <Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl
                                fullWidth
                                error={!!errors.transaction_type}
                            >
                                <InputLabel id="demo-simple-select-label">
                                    Transaction Type *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select22"
                                    label="Transaction Type"
                                    name="transaction_type"
                                    value={state?.transaction_type}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                >
                                    <MenuItem value={'buy'}>Buy</MenuItem>
                                    <MenuItem value={'sell'}>Sell</MenuItem>
                                </Select>
                                {errors.transaction_type && (
                                    <span className="text-red-500 text-xs">
                                        {errors.transaction_type}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.trade_type}>
                                <InputLabel id="demo-simple-select-label">
                                    Trade Type *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Trade Type"
                                    name="trade_type"
                                    value={state?.trade_type}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                >
                                    <MenuItem value={'Physical'}>
                                        Physical
                                    </MenuItem>
                                    <MenuItem value={'Financial'}>
                                        Financial
                                    </MenuItem>
                                </Select>
                                {errors.trade_type && (
                                    <span className="text-red-500 text-xs">
                                        {errors.trade_type}
                                    </span>
                                )}
                            </FormControl>
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl fullWidth error={!!errors.trader}>
                                <InputLabel id="demo-simple-select-label">
                                    Trader *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Trader"
                                    name="trader"
                                    value={state?.trader}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    {allUsers?.map((user, index) => (
                                        <MenuItem key={index} value={user}>
                                            {user.fullname}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.trader && (
                                    <span className="text-red-500 text-xs">
                                        {errors.trader}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.portfolio}>
                                <InputLabel id="demo-simple-select-label">
                                    Portfolio *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Portfolio"
                                    name="portfolio"
                                    value={state?.portfolio}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                >
                                    {portfolio?.map((pf, index) => (
                                        <MenuItem key={index} value={pf}>
                                            {pf.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.portfolio && (
                                    <span className="text-red-500 text-xs">
                                        {errors.portfolio}
                                    </span>
                                )}
                            </FormControl>
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl
                                fullWidth
                                error={!!errors.counterparty}
                            >
                                <InputLabel id="demo-simple-select-label">
                                    Counterparty *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Counterparty"
                                    name="counterparty"
                                    value={state?.counterparty}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    {counterParty?.map((cp, index) => (
                                        <MenuItem key={index} value={cp}>
                                            {cp.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.counterparty && (
                                    <span className="text-red-500 text-xs">
                                        {errors.counterparty}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl fullWidth={true}>
                                <InputLabel id="referenceLabel">
                                    Reference
                                </InputLabel>
                                <OutlinedInput
                                    id="reference"
                                    label="Reference"
                                    name="reference"
                                    value={state?.reference}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <FormControl
                                    fullWidth
                                    error={!!errors.trade_date}
                                >
                                    <DatePicker
                                        label="Trade Date *"
                                        format="DD/MM/YYYY"
                                        maxDate={dayjs()}
                                        value={
                                            state?.trade_date
                                                ? dayjs(state?.trade_date)
                                                : null
                                        }
                                        onChange={(value) =>
                                            handleDateChange(
                                                'trade_date',
                                                value
                                            )
                                        }
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                name: 'trade_date',
                                                placeholder: 'DD/MM/YYYY',
                                                error: !!errors.trade_date,
                                                onBlur: handleBlur
                                                // required: true
                                            }
                                        }}
                                    />
                                    {errors.trade_date && (
                                        <span className="text-red-500 text-xs">
                                            {errors.trade_date}
                                        </span>
                                    )}
                                </FormControl>
                            </LocalizationProvider>
                            <FormControl fullWidth error={!!errors.exchange}>
                                <InputLabel id="demo-simple-select-label">
                                    Exchange *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Exchange"
                                    name="exchange"
                                    value={state?.exchange}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    {exchanges?.map((exchange, index) => (
                                        <MenuItem key={index} value={exchange}>
                                            {exchange.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.exchange && (
                                    <span className="text-red-500 text-xs">
                                        {errors.exchange}
                                    </span>
                                )}
                            </FormControl>
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl fullWidth error={!!errors.broker}>
                                <InputLabel id="demo-simple-select-label">
                                    Broker *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Broker"
                                    name="broker"
                                    value={state?.broker}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    {brokers?.map((broker, index) => (
                                        <MenuItem key={index} value={broker}>
                                            {broker.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.broker && (
                                    <span className="text-red-500 text-xs">
                                        {errors.broker}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.status}>
                                <InputLabel id="demo-simple-select-label">
                                    Status *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Status"
                                    name="status"
                                    value={state?.status}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                >
                                    <MenuItem value={'New'}>New</MenuItem>
                                    <MenuItem value={'Pending'}>
                                        Pending
                                    </MenuItem>
                                </Select>
                                {errors.status && (
                                    <span className="text-red-500 text-xs">
                                        {errors.status}
                                    </span>
                                )}
                            </FormControl>
                        </Box>
                        <Box marginBottom={2}>
                            <FormControl fullWidth={true} variant="outlined">
                                <InputLabel
                                    id="commentLabel"
                                    sx={{ bgcolor: '#FFFFFF' }}
                                >
                                    Comments and Conditions
                                </InputLabel>
                                <OutlinedInput
                                    id="comments"
                                    label="Comments"
                                    name="comments"
                                    value={state?.comments}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl fullWidth error={!!errors.price_type}>
                                <InputLabel id="demo-simple-select-label">
                                    Price Type *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Price Type"
                                    name="price_type"
                                    value={state?.price_type}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                >
                                    {price_type_options?.map(
                                        (option, index) => (
                                            <MenuItem
                                                key={index}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                                {errors.price_type && (
                                    <span className="text-red-500 text-xs">
                                        {errors.price_type}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.endpoint}>
                                <InputLabel id="demo-simple-select-label">
                                    Endpoint *
                                    {/* {state?.trade_type === 'Financial' ? '*' : '' || state?.trade_type === 'Physical' ? '*' : ''} */}
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Endpoint"
                                    name="endpoint"
                                    value={state?.endpoint}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    {user?.Endpoints?.map((endpoint, index) => (
                                        <MenuItem key={index} value={endpoint}>
                                            {endpoint.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.endpoint && (
                                    <span className="text-red-500 text-xs">
                                        {errors.endpoint}
                                    </span>
                                )}
                            </FormControl>
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl fullWidth>
                                <InputLabel shrink htmlFor="payment-frequency">
                                    Payment Frequency *
                                </InputLabel>
                                <OutlinedInput
                                    id="payment-frequency"
                                    notched
                                    label="Payment Frequency"
                                    value="Monthly"
                                    inputProps={{ readOnly: true }}
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">
                                    Currency *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Currency"
                                    name="currency"
                                    value={state?.currency}
                                    onChange={handleChange}
                                    disabled
                                    required
                                >
                                    <MenuItem value={'EUR'}>EUR</MenuItem>
                                    <MenuItem value={'CHF'}>CHF</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl
                                fullWidth
                                error={!!errors.delivery_period_sym}
                            >
                                <InputLabel
                                    id="demo-simple-select-label"
                                    sx={{ bgcolor: '#FFFFFF', pr: 1 }}
                                >
                                    Delivery Period *
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Delivery Period"
                                    name="delivery_period_sym"
                                    value={state?.delivery_period_sym}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                >
                                    {delivery_period_options?.map(
                                        (option, index) => (
                                            <MenuItem
                                                key={index}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                                {errors.delivery_period_sym && (
                                    <span className="text-red-500 text-xs">
                                        {errors.delivery_period_sym}
                                    </span>
                                )}
                            </FormControl>
                            {state?.delivery_period_sym === 'DA' ||
                            state?.delivery_period_sym === 'BOM' ||
                            state?.delivery_period_sym === 'MANUAL' ? (
                                <FormControl
                                    fullWidth
                                    error={!!errors.start_date}
                                >
                                    <InputLabel
                                        htmlFor="start_date"
                                        shrink
                                        sx={{ bgcolor: '#F5F5F5' }}
                                    >
                                        Start Date *
                                    </InputLabel>
                                    <OutlinedInput
                                        id="start_date"
                                        type="date"
                                        value={state?.start_date}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        name="start_date"
                                        required
                                    />
                                    {errors.start_date && (
                                        <span className="text-red-500 text-xs">
                                            {errors.start_date}
                                        </span>
                                    )}
                                </FormControl>
                            ) : null}
                            {state?.delivery_period_sym === 'MANUAL' && (
                                <FormControl
                                    fullWidth
                                    error={!!errors.end_date}
                                >
                                    <InputLabel
                                        htmlFor="end_date"
                                        shrink
                                        sx={{ bgcolor: '#F5F5F5' }}
                                    >
                                        End Date *
                                    </InputLabel>
                                    <OutlinedInput
                                        id="end_date"
                                        type="date"
                                        value={state?.end_date}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        name="end_date"
                                        required
                                    />
                                    {errors.end_date && (
                                        <span className="text-red-500 text-xs">
                                            {errors.end_date}
                                        </span>
                                    )}
                                </FormControl>
                            )}
                            {state?.delivery_period_sym === 'MONTH' ||
                            state?.delivery_period_sym === 'SUMMER' ||
                            state?.delivery_period_sym === 'WINTER' ||
                            state?.delivery_period_sym === 'Q1' ||
                            state?.delivery_period_sym === 'Q2' ||
                            state?.delivery_period_sym === 'Q3' ||
                            state?.delivery_period_sym === 'Q4' ||
                            state?.delivery_period_sym === 'CAL' ||
                            state?.delivery_period_sym === 'GY' ? (
                                <FormControl
                                    fullWidth
                                    error={!!errors.delivery_period_year}
                                >
                                    <InputLabel id="demo-simple-select-label">
                                        Year *
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        label="Month"
                                        name="delivery_period_year"
                                        value={state?.delivery_period_year}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        required
                                    >
                                        {years_options?.map((option, index) => (
                                            <MenuItem
                                                key={index}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.delivery_period_year && (
                                        <span className="text-red-500 text-xs">
                                            {errors.delivery_period_year}
                                        </span>
                                    )}
                                </FormControl>
                            ) : null}
                            {state?.delivery_period_sym === 'MONTH' ? (
                                <FormControl
                                    fullWidth
                                    error={!!errors.delivery_period_month}
                                >
                                    <InputLabel id="demo-simple-select-label">
                                        Month
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        label="Month"
                                        name="delivery_period_month"
                                        value={state?.delivery_period_month}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        required
                                    >
                                        {months_options?.map(
                                            (option, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                    {errors.delivery_period_month && (
                                        <span className="text-red-500 text-xs">
                                            {errors.delivery_period_month}
                                        </span>
                                    )}
                                </FormControl>
                            ) : null}
                        </Box>
                        <Box className="flex gap-3" marginBottom={2}>
                            <FormControl
                                fullWidth={true}
                                variant="outlined"
                                error={
                                    !!errors.capacity ||
                                    (state?.capacity !== '' &&
                                        Number(state?.capacity) <= 0)
                                }
                            >
                                <InputLabel
                                    id="capacityLabel"
                                    sx={{
                                        bgcolor: '#FFFFFF',
                                        p: '0px 2px 0px'
                                    }}
                                >
                                    Capacity (MW) *
                                </InputLabel>
                                <OutlinedInput
                                    id="capacity"
                                    label="Capacity"
                                    type="number"
                                    onKeyDown={(e) =>
                                        e.key === 'e' && e.preventDefault()
                                    }
                                    name="capacity"
                                    value={state?.capacity}
                                    onChange={handleNumericChange}
                                    onBlur={handleBlur}
                                    disabled={state?.volume !== ''}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    endAdornment={
                                        state?.volume !== '' ? (
                                            <span style={{ color: 'red' }}>
                                                Locked
                                            </span>
                                        ) : null
                                    }
                                />
                                <FormHelperText>
                                    {errors.capacity ||
                                        (state?.capacity !== '' &&
                                        Number(state?.capacity) <= 0
                                            ? 'Capacity must be greater than 0'
                                            : '')}
                                </FormHelperText>
                            </FormControl>
                            <FormControl
                                fullWidth={true}
                                variant="outlined"
                                error={
                                    !!errors.volume ||
                                    (state?.volume !== '' &&
                                        Number(state?.volume) <= 0)
                                }
                            >
                                <InputLabel
                                    id="volumeLabel"
                                    sx={{ bgcolor: '#FFFFFF' }}
                                >
                                    Volume (MW) *
                                </InputLabel>
                                <OutlinedInput
                                    id="volume"
                                    label="Volume (MW)"
                                    type="number"
                                    name="volume"
                                    onKeyDown={(e) =>
                                        e.key === 'e' && e.preventDefault()
                                    }
                                    value={state?.volume}
                                    onChange={handleNumericChange}
                                    onBlur={handleBlur}
                                    disabled={state?.capacity !== ''}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    endAdornment={
                                        state?.capacity !== '' ? (
                                            <span style={{ color: 'red' }}>
                                                Locked
                                            </span>
                                        ) : null
                                    }
                                />
                                <FormHelperText>
                                    {errors.volume ||
                                        (state?.volume !== '' &&
                                        Number(state?.volume) <= 0
                                            ? 'Volume must be greater than 0'
                                            : '')}
                                </FormHelperText>
                            </FormControl>
                        </Box>

                        <FormControl
                            fullWidth={true}
                            variant="outlined"
                            error={
                                !!errors.price ||
                                (state?.price_type !== 'DA Index' &&
                                    state?.price_type !== 'MA Index' &&
                                    state?.price !== '' &&
                                    Number(state?.price) < 0)
                            }
                        >
                            <InputLabel
                                id="priceLabel"
                                sx={{ bgcolor: '#FFFFFF' }}
                            >
                                {state?.price_type === 'DA Index' ||
                                state?.price_type === 'MA Index'
                                    ? 'Price'
                                    : 'Price *'}
                            </InputLabel>
                            <OutlinedInput
                                id="price"
                                label="Price"
                                type="number"
                                name="price"
                                disabled={
                                    state?.price_type === 'DA Index' ||
                                    state?.price_type === 'MA Index'
                                }
                                onKeyDown={(e) =>
                                    e.key === 'e' && e.preventDefault()
                                }
                                value={state?.price}
                                onChange={handleNumericChange}
                                onBlur={handleBlur}
                                inputProps={{ min: 0, step: 0.001 }}
                                endAdornment={
                                    state?.price_type === 'DA Index' ||
                                    state?.price_type === 'MA Index' ? (
                                        <span style={{ color: 'red' }}>
                                            Locked
                                        </span>
                                    ) : null
                                }
                            />
                            <FormHelperText>
                                {errors.price ||
                                    (state?.price_type !== 'DA Index' &&
                                    state?.price_type !== 'MA Index' &&
                                    state?.price !== '' &&
                                    Number(state?.price) < 0
                                        ? 'Price cannot be negative'
                                        : '')}
                            </FormHelperText>
                        </FormControl>
                    </Box>
                    <Box className="flex justify-end mt-2 gap-3">
                        <Button
                            variant="outlined"
                            color="error"
                            sx={{
                                borderColor: '1px solid #EB6969',
                                color: '#EB6969',
                                fontSize: '14px',
                                '&:hover': {
                                    borderColor: '1px solid #EB6969',
                                    color: 'white',
                                    backgroundColor: '#EB6969'
                                }
                            }}
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            type="submit"
                            variant="contained"
                            className="page-button"
                            sx={{
                                bgcolor: '#0b9cfd',
                                '&:hover': {
                                    bgcolor: '#0b9cfd'
                                }
                            }}
                            disabled={
                                //state.volume and state.capacity are mutually exclusive, so we need to check if both are empty
                                (state?.volume === '' &&
                                    state?.capacity === '') ||
                                // state?.price === '' ||
                                state?.trade_type === '' ||
                                state?.transaction_type === '' ||
                                state?.trader === '' ||
                                state?.portfolio === '' ||
                                state?.counterparty === '' ||
                                state?.exchange === '' ||
                                state?.broker === '' ||
                                state?.status === '' ||
                                state?.price_type === '' ||
                                state?.endpoint === '' ||
                                state?.currency === '' ||
                                (state?.delivery_period_sym === 'MANUAL'
                                    ? state?.start_date === '' ||
                                      state?.end_date === ''
                                    : state?.delivery_period_sym === 'DA' ||
                                        state?.delivery_period_sym === 'BOM'
                                      ? state?.start_date === ''
                                      : state?.delivery_period_year === '') ||
                                state?.delivery_period_sym === '' ||
                                //disable submit button in delivery_period_sym is MONTH and delivery_period_month is empty
                                (state?.delivery_period_sym === 'MONTH' &&
                                    state?.delivery_period_month === '') ||
                                // price is required only for Fixed; DA Index / MA Index prices are indexed
                                (state?.price_type === 'Fixed' &&
                                    (state?.price === '' ||
                                        state?.price < 0)) ||
                                (state?.capacity !== '' &&
                                    Number(state?.capacity) <= 0) ||
                                (state?.volume !== '' &&
                                    Number(state?.volume) <= 0)
                            }
                        >
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default Forward;
